import * as React from 'react'
import {
  AspectOption,
  aspectOptions,
  DimensionVariantImage
} from './DimensionOptions'
import { isAspectRatioValid } from './dimensionUtils'

interface DimensionOptionsArgs {
  initialDimension?: AspectOption
  initialImages?: Record<string, DimensionVariantImage>
}

export const useDimensionOptions = ({
  initialDimension = aspectOptions[0], // Default to 1.91:1 (option1)
  initialImages = {}
}: DimensionOptionsArgs = {}) => {
  // State for the currently selected dimension option
  const [selectedDimension, setSelectedDimension] = React.useState<AspectOption>(initialDimension)

  // State for dimension-specific images
  const [dimensionImages, setDimensionImages] = React.useState<Record<string, DimensionVariantImage>>(initialImages)

  // Refs to track image elements for each dimension
  const imageRefs = React.useRef<Record<string, HTMLImageElement | null>>({})

  // Refs to track container elements for each dimension
  const containerRefs = React.useRef<Record<string, HTMLDivElement | null>>({})

  // Handler for dimension selection
  const handleDimensionSelect = React.useCallback((option: AspectOption) => {
    setSelectedDimension(option)
  }, [])

  // Get the current dimension image or fall back to undefined
  const getCurrentDimensionImage = React.useCallback(() => {
    return dimensionImages[selectedDimension.id]
  }, [dimensionImages, selectedDimension.id])

  // Validate all dimension images against their expected aspect ratios
  const validateDimensionImages = React.useCallback(() => {
    const updatedImages = { ...dimensionImages }
    let hasChanges = false

    aspectOptions.forEach(option => {
      const image = updatedImages[option.id]
      if (image) {
        const isValid = isAspectRatioValid(image.width, image.height, option.ratio)
        if (image.isAspectRatioValid !== isValid) {
          updatedImages[option.id] = {
            ...image,
            isAspectRatioValid: isValid
          }
          hasChanges = true
        }
      }
    })

    if (hasChanges) {
      setDimensionImages(updatedImages)
    }
  }, [dimensionImages])

  // Check if all dimension images have valid aspect ratios
  const areAllAspectRatiosValid = React.useCallback(() => {
    // If there are no dimension images, return false
    if (Object.keys(dimensionImages).length === 0) return false

    // Check if all dimension images have valid aspect ratios
    return aspectOptions.every(option => {
      const image = dimensionImages[option.id]
      // If the image doesn't exist, consider it invalid
      if (!image) return false
      // If isAspectRatioValid is explicitly false, it's invalid
      // Otherwise, if undefined or true, consider it valid
      return image.isAspectRatioValid !== false
    })
  }, [dimensionImages])

  // Update a specific dimension image
  const updateDimensionImage = React.useCallback((dimensionId: string, imageData: {
    url: string
    width: number
    height: number
  }) => {
    // Find the corresponding aspect option to get the expected ratio
    const option = aspectOptions.find(opt => opt.id === dimensionId)
    const isValid = option ?
      isAspectRatioValid(imageData.width, imageData.height, option.ratio) :
      false

    setDimensionImages(prev => ({
      ...prev,
      [dimensionId]: {
        url: imageData.url,
        width: imageData.width,
        height: imageData.height,
        isAspectRatioValid: isValid
      }
    }))
  }, [])

  // Initialize all dimension variants with the same image
  const initializeDimensionImages = React.useCallback((baseImage: {
    url: string
    width: number
    height: number
  }) => {
    const initialDimensionImages: Record<string, DimensionVariantImage> = {}
    aspectOptions.forEach(option => {
      const isValid = isAspectRatioValid(baseImage.width, baseImage.height, option.ratio)
      initialDimensionImages[option.id] = {
        url: baseImage.url,
        width: baseImage.width,
        height: baseImage.height,
        isAspectRatioValid: isValid
      }
    })
    setDimensionImages(initialDimensionImages)
  }, [])

  // Reset dimension images to empty
  const resetDimensionImages = React.useCallback(() => {
    setDimensionImages({})
  }, [])

  // Register an image element ref for a specific dimension
  const registerImageRef = React.useCallback((dimensionId: string, element: HTMLImageElement | null) => {
    imageRefs.current[dimensionId] = element
  }, [])

  // Register a container element ref for a specific dimension
  const registerContainerRef = React.useCallback((dimensionId: string, element: HTMLDivElement | null) => {
    containerRefs.current[dimensionId] = element
  }, [])

  // Validate a specific dimension's aspect ratio based on the actual rendered image
  const validateRenderedImage = React.useCallback((dimensionId: string) => {
    const imageElement = imageRefs.current[dimensionId]
    if (!imageElement) return

    const option = aspectOptions.find(opt => opt.id === dimensionId)
    if (!option) return
    const image = dimensionImages[dimensionId]
    if (!image) return

    // Get the natural dimensions of the image
    const naturalWidth = imageElement.naturalWidth
    const naturalHeight = imageElement.naturalHeight

    // Only proceed if we have valid dimensions
    if (naturalWidth <= 0 || naturalHeight <= 0) return

    // Check if the aspect ratio is valid
    const isValid = isAspectRatioValid(naturalWidth, naturalHeight, option.ratio)

    // Update if the validation status has changed
    if (image.isAspectRatioValid !== isValid) {
      setDimensionImages(prev => ({
        ...prev,
        [dimensionId]: {
          ...prev[dimensionId],
          isAspectRatioValid: isValid,
          // Update width and height to match the natural dimensions
          width: naturalWidth,
          height: naturalHeight
        }
      }))
    }
  }, [dimensionImages])

  // Monitor image loading events
  React.useEffect(() => {
    // Set up image load listeners for each dimension
    const imageLoadListeners: Record<string, EventListener> = {}

    Object.entries(dimensionImages).forEach(([dimensionId]) => {
      const imageElement = imageRefs.current[dimensionId]
      if (!imageElement) return

      // Create a load listener for this image
      const loadListener = () => validateRenderedImage(dimensionId)

      // Attach the listener
      imageElement.addEventListener('load', loadListener)
      imageLoadListeners[dimensionId] = loadListener

      // If the image is already loaded, validate it now
      if (imageElement.complete) {
        validateRenderedImage(dimensionId)
      }
    })

    // Clean up listeners on unmount
    return () => {
      Object.entries(imageLoadListeners).forEach(([dimensionId, listener]) => {
        const imageElement = imageRefs.current[dimensionId]
        if (imageElement) {
          imageElement.removeEventListener('load', listener)
        }
      })
    }
  }, [dimensionImages, validateRenderedImage])

  // Set up ResizeObserver to monitor container size changes
  React.useEffect(() => {
    // Skip if ResizeObserver is not available (older browsers)
    if (typeof ResizeObserver === 'undefined') return

    const observers: ResizeObserver[] = []

    // Create observers for each container
    Object.entries(containerRefs.current).forEach(([dimensionId, container]) => {
      if (!container) return

      const observer = new ResizeObserver(() => {
        // Container size changed, validate the image
        validateRenderedImage(dimensionId)
      })

      observer.observe(container)
      observers.push(observer)
    })

    // Clean up observers on unmount
    return () => {
      observers.forEach(observer => observer.disconnect())
    }
  }, [validateRenderedImage])

  // Validate dimension images when the component mounts or when dimensionImages changes
  React.useEffect(() => {
    // Only run validation if we have dimension images
    if (Object.keys(dimensionImages).length > 0) {
      validateDimensionImages()
    }
  }, [validateDimensionImages])

  return {
    selectedDimension,
    dimensionImages,
    handleDimensionSelect,
    getCurrentDimensionImage,
    updateDimensionImage,
    initializeDimensionImages,
    resetDimensionImages,
    setDimensionImages,
    validateDimensionImages,
    areAllAspectRatiosValid,
    // Export ref registration functions
    registerImageRef,
    registerContainerRef
  }
}
